import classNames from 'classnames';
import { ListItem } from 'components/actions/ListItem';
import { ListItemContainer } from 'components/containers/ListItemContainer';
import { Input } from 'components/forms/Input';
import { Select } from 'components/forms/Select';
import { SelectDropdownTemplate, SelectItem, SelectValueTemplate } from 'components/forms/Select/Select.view';
import { InputTypes } from 'constants/form';
import { SquareButtonIconsEnum } from 'constants/square-button-icons';
import { useBreakpoints } from 'hooks/useBreakpoints';
import React, { useCallback } from 'react';
import { RegisterOptions, UseFormReturn } from 'react-hook-form';
import classes from './SelectOrType.module.scss';

export type SelectOrTypeProps = {
  disabled: boolean;
  formInstance: UseFormReturn;
  name: string;
  label?: string;
  placeholder: string;
  items: SelectItem[];
  rules: RegisterOptions;
  size: 'large' | 'medium' | 'small';
};

export const SelectOrType = ({
  formInstance,
  name,
  label,
  disabled,
  placeholder,
  items,
  rules,
  size
}: SelectOrTypeProps): JSX.Element => {
  const breakpoint = useBreakpoints();
  const valueTemplate: SelectValueTemplate = useCallback(
    ({ setExpanded, disabled, name, rules, size, useFormInstance }) => {
      return (
        <Input
          disabled={disabled}
          inputWrapperClassName={classes.ValueTemplateContainer}
          placeholder={placeholder}
          name={name}
          formInstance={useFormInstance}
          action={() => setExpanded(prevState => !disabled && !prevState)}
          rules={rules}
          size={size}
          actionButton={{ icon: SquareButtonIconsEnum.arrowDown }}
          type={InputTypes.text}
        />
      );
    },
    [placeholder]
  );

  const dropdownTemplate: SelectDropdownTemplate = useCallback(
    (args, globalClasses) => {
      const { expanded, setExpanded, disabled, name, label, useFormInstance, items } = args;
      const onClickList = (item: SelectItem | string) => {
        const selectValue = typeof item === 'string' ? item : item.value;
        setExpanded(prevState => !prevState);
        useFormInstance.setValue(name, selectValue);
      };
      return breakpoint !== 'small' ? (
        <div className={globalClasses}>
          <ListItemContainer type={'ul'} expanded={expanded}>
            {items.map((item, index) => (
              <ListItem
                key={`${typeof item === 'string' ? item : item.label}-${index}`}
                role={'option'}
                aria-label={label}
                variation={'regular'}
                type={'li'}
                selected={false}
                action={() => {
                  onClickList(item);
                }}
                disabled={!!disabled}>
                {typeof item === 'string' ? item : item.label}
              </ListItem>
            ))}
          </ListItemContainer>
        </div>
      ) : (
        <>
          {expanded ? (
            <div className={classNames(classes.DropdownContainer, globalClasses)}>
              <ListItemContainer type={'div'} expanded={expanded}>
                {items.map((item, index) => (
                  <ListItem
                    key={`${typeof item === 'string' ? item : item.label}-${index}`}
                    role={'option'}
                    aria-label={label}
                    variation={'regular'}
                    type={'li'}
                    selected={false}
                    action={() => onClickList(item)}
                    disabled={!!disabled}>
                    {typeof item === 'string' ? item : item.label}
                  </ListItem>
                ))}
              </ListItemContainer>
            </div>
          ) : null}
        </>
      );
    },
    [breakpoint]
  );

  return (
    <Select
      disabled={disabled}
      items={items}
      label={label}
      name={name}
      size={size}
      rules={rules}
      overrideIntent={'mouse'}
      useFormInstance={formInstance}
      valueTemplate={valueTemplate}
      dropdownTemplate={dropdownTemplate}
    />
  );
};
